
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Users } from "lucide-react";
import UserSearchModal from "./UserSearchModal";

export default function CreateChatRoomForm() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [roomName, setRoomName] = useState("");
  const [description, setDescription] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdRoomId, setCreatedRoomId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("You must be logged in to create a chat room");
      return;
    }

    if (!roomName.trim()) {
      toast.error("Room name is required");
      return;
    }

    try {
      setCreating(true);
      
      const { data, error } = await supabase
        .from("chat_rooms")
        .insert({
          name: roomName.trim(),
          description: description.trim() || null,
          is_private: isPrivate,
          created_by: user.id,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        toast.error("Failed to create chat room");
        console.error("Error creating chat room:", error);
        return;
      }

      // Add creator as a member for private rooms
      if (isPrivate) {
        try {
          // Temporarily commented out until types are updated
          // await supabase
          //   .from("chat_room_members")
          //   .insert({
          //     room_id: data.id,
          //     user_id: user.id,
          //     role: "admin"
          //   });
          console.log("Would add creator as admin member for room:", data.id);
        } catch (memberError) {
          console.error("Error adding creator as member:", memberError);
          // Don't fail the room creation if member addition fails
        }
      }

      toast.success("Chat room created successfully!");
      setCreatedRoomId(data.id);
      
      // Navigate to the new chat room
      navigate(`/chat-rooms/${data.id}`);
      
    } catch (err) {
      toast.error("Failed to create chat room");
      console.error("Error creating chat room:", err);
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setRoomName("");
    setDescription("");
    setIsPrivate(false);
    setCreatedRoomId(null);
  };

  const handleUserAdded = () => {
    // Room is already created, just refresh
    console.log("User added to newly created room");
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">You must be logged in to create a chat room.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>Create New Chat Room</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="roomName">Room Name</Label>
            <Input
              id="roomName"
              type="text"
              value={roomName}
              onChange={(e) => setRoomName(e.target.value)}
              placeholder="Enter room name..."
              disabled={creating}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter room description..."
              disabled={creating}
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Switch
              id="isPrivate"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
              disabled={creating}
            />
            <Label htmlFor="isPrivate" className="text-sm">
              Private Room
            </Label>
          </div>
          
          <div className="flex space-x-2">
            <Button 
              type="submit" 
              disabled={creating || !roomName.trim()}
              className="flex-1"
            >
              {creating ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <span>Creating...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Plus className="h-4 w-4" />
                  <span>Create Room</span>
                </div>
              )}
            </Button>
            
            {isPrivate && createdRoomId && (
              <UserSearchModal 
                roomId={createdRoomId} 
                onUserAdded={handleUserAdded}
              >
                <Button type="button" variant="outline" size="icon">
                  <Users className="h-4 w-4" />
                </Button>
              </UserSearchModal>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
